import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, message, Modal, Select, Space, Table, Tag, Image } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { deleteFeedback, getFeedbacks, type AdminFeedback } from '../services/api';

const { Option } = Select;

const formatTime = (timestamp: number) => new Date(timestamp * 1000).toLocaleString('zh-CN');

const FeedbackManage: React.FC = () => {
  const [data, setData] = useState<AdminFeedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [feedbackType, setFeedbackType] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const loadData = useCallback(async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await getFeedbacks(nextPage, nextPageSize, {
        keyword: keyword.trim() || undefined,
        feedbackType: feedbackType === 'all' ? undefined : feedbackType,
      });
      if (res.code !== 0) {
        message.error(res.message || '加载反馈失败');
        return;
      }
      setData(res.data.list);
      setTotal(res.data.total);
    } catch (e) {
      message.error('加载反馈失败');
    } finally {
      setLoading(false);
    }
  }, [feedbackType, keyword, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '删除后无法恢复，确定要删除这条反馈吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await deleteFeedback(id);
          if (res.code !== 0) {
            message.error(res.message || '删除失败');
            return;
          }
          message.success('删除成功');
          loadData();
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  const columns = [
    {
      title: '反馈时间',
      dataIndex: 'created_at',
      width: 170,
      render: (value: number) => formatTime(value),
    },
    {
      title: '用户',
      width: 180,
      render: (_: unknown, record: AdminFeedback) => (
        <div>
          <div>{record.nickname || `用户#${record.user_id}`}</div>
          <div style={{ color: '#999', fontSize: 12 }}>{record.phone || '-'}</div>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'feedback_type',
      width: 90,
      render: (value: string) => value === 'bug' ? <Tag color="error">错误</Tag> : <Tag color="green">建议</Tag>,
    },
    {
      title: '内容',
      dataIndex: 'content',
      ellipsis: true,
    },
    {
      title: '截图',
      dataIndex: 'images',
      width: 220,
      render: (images: string[]) => {
        if (!images || images.length === 0) return <span style={{ color: '#999' }}>无</span>;
        return (
          <Image.PreviewGroup>
            <Space size={6}>
              {images.slice(0, 3).map((url, idx) => (
                <Image key={`${url}-${idx}`} src={url} width={52} height={52} style={{ objectFit: 'cover', borderRadius: 6 }} />
              ))}
            </Space>
          </Image.PreviewGroup>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_: unknown, record: AdminFeedback) => (
        <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input
          allowClear
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索用户/手机号/内容"
          style={{ width: 260 }}
          onPressEnter={() => {
            setPage(1);
            loadData(1, pageSize);
          }}
        />
        <Select value={feedbackType} style={{ width: 140 }} onChange={setFeedbackType}>
          <Option value="all">全部类型</Option>
          <Option value="suggest">建议</Option>
          <Option value="bug">错误</Option>
        </Select>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={() => {
            setPage(1);
            loadData(1, pageSize);
          }}
        >
          查询
        </Button>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            const size = nextPageSize || pageSize;
            setPage(nextPage);
            setPageSize(size);
            loadData(nextPage, size);
          },
        }}
      />
    </div>
  );
};

export default FeedbackManage;
